import { useEffect, useCallback, useState, useRef } from 'react';
import { webSocketManager } from '../services/websocket';

interface PaymentMethod {
  id: string;
  type: 'card';
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  created: number;
}

export function useWebSocketPaymentMethods(onPaymentMethodsChanged: () => void) {
  const [isConnected, setIsConnected] = useState(false);
  const lastEventTimestamp = useRef<string | null>(null);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  // Memoize the message handler to prevent recreation
  const handleMessage = useCallback((data: any) => {
    console.log('[WebSocketPaymentMethods] Received payment methods change event:', data);
    console.log('[WebSocketPaymentMethods] WebSocket connection status:', webSocketManager.getStatus());

    // Deduplicate events based on timestamp
    if (data.timestamp && data.timestamp === lastEventTimestamp.current) {
      console.log('[WebSocketPaymentMethods] Duplicate event detected, skipping:', data.timestamp);
      return;
    }

    lastEventTimestamp.current = data.timestamp;

    // Debounce rapid events (within 500ms) to prevent multiple API calls
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      console.log('[WebSocketPaymentMethods] Triggering payment methods refresh after debounce');
      onPaymentMethodsChanged();
      debounceTimeout.current = null;
    }, 500);
  }, [onPaymentMethodsChanged]);

  useEffect(() => {
    console.log('[WebSocketPaymentMethods] Setting up listener for payment methods change events');
    console.log('[WebSocketPaymentMethods] Initial WebSocket status:', webSocketManager.getStatus());

    const unsubscribe = webSocketManager.onMessage('paymentMethods.changed', handleMessage);

    // Monitor connection status
    const cleanupStatusListener = webSocketManager.onStatusChange((status) => {
      console.log('[WebSocketPaymentMethods] WebSocket status changed to:', status);
      setIsConnected(status === 'connected');
    });

    // Initial check
    setIsConnected(webSocketManager.getStatus() === 'connected');

    return () => {
      console.log('[WebSocketPaymentMethods] Cleaning up listener');
      unsubscribe();
      cleanupStatusListener();

      // Clean up debounce timeout
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
        debounceTimeout.current = null;
      }
    };
  }, [handleMessage]); // Only recreate when the memoized callback changes

  return { isConnected };
}
